// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import { Button, Checkbox, Divider, Form, Popconfirm, Select } from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  QuestionCircleOutlined,
  UpOutlined
} from "@ant-design/icons/lib";
import { useTranslation } from "react-i18next";
import {
  QuestionTypes,
  SurveyAddQuestionButton
} from "./SurveyAddQuestionButton";
import {
  Answer,
  SurveyBlockProps,
  SurveyWorkflowType
} from "../../../types/SurveyTypes";
import FormField from "../../../components/FormField";
import { PlusCircleOutlined } from "@ant-design/icons";
import { AnswerBlock } from "./AnswerBlock";
import shortid from "shortid";

export const QuestionBlock: React.FunctionComponent<SurveyBlockProps> = (
  props: SurveyBlockProps
) => {
  const { t } = useTranslation();

  const {
    question,
    indexQuestion,
    handleUpdateQuestion,
    handleDeleteQuestion,
    handleAddQuestion,
    handleAscendQuestion,
    handleDescendQuestion,
    editMode
  } = props;

  const handleAddAnswer = (answer: Answer) => {
    question.answerChoice.push(answer);
    handleUpdateQuestion(question, indexQuestion);
  };

  const handleUpdateAnswer = (answer: Answer, index: number) => {
    const newAnswers = [...question.answerChoice];
    newAnswers[index] = answer;
    question.answerChoice = newAnswers;
    handleUpdateQuestion(question, indexQuestion);
  };

  const handleDeleteAnswer = (index: number) => {
    const newAnswers = [...question.answerChoice];
    newAnswers.splice(index, 1);
    question.answerChoice = newAnswers;
    handleUpdateQuestion(question, indexQuestion);
  };

  const handleUpdateIsQuestionOptional = () => {
    question.optional = !question?.optional;
    handleUpdateQuestion(question, indexQuestion);
  };

  const renderQuestionForm = (indexQuestion: number) => {
    return (
      <>
        <FormField
          labelName={t("survey.add_question.question")}
          defaultValue={question.label}
          required={true}
          emptyErrorMessage={t("global.empty_error_field")}
          readOnly={!editMode}
          onChange={text => {
            question.label = text.target.value.trim();
          }}
        />
        {question.type === SurveyWorkflowType.multiple && (
          <Form.Item
            name="maxAnswers"
            key="maxAnswers"
            label={<span>{t("survey.add_question.max_answers.title")}</span>}
            initialValue={question?.maxAnswers}
          >
            <Select
              id="type"
              placeholder={t("survey.add_question.max_answers.placeholder")}
              disabled={!editMode}
              onChange={(value: string) => {
                question.maxAnswers =
                  value === "" ? undefined : parseInt(value);
                handleUpdateQuestion(question, indexQuestion);
              }}
            >
              <Select.Option key={0} value={""}>
                {" "}
              </Select.Option>
              {question.answerChoice.map((value, index) => (
                <Select.Option key={index + 1} value={index + 1}>
                  {index + 1}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {(question.type === SurveyWorkflowType.simple ||
          question.type === SurveyWorkflowType.multiple) &&
          question.answerChoice.map((answer, indexAnswer) => (
            <AnswerBlock
              key={`nbo-question${indexQuestion}-answerChoice${indexAnswer}`}
              indexQuestion={indexQuestion}
              handleDeleteAnswer={id => handleDeleteAnswer(id)}
              handleUpdateAnswer={(answer, index) =>
                handleUpdateAnswer(answer, index)
              }
              answer={answer}
              indexAnswer={indexAnswer}
              editMode={editMode}
            />
          ))}
        {editMode &&
          (question.type === SurveyWorkflowType.simple ||
            question.type === SurveyWorkflowType.multiple) && (
            <Button
              className="btn-secondary m-2"
              onClick={() => {
                handleAddAnswer({
                  label: "",
                  id: shortid.generate()
                });
              }}
            >
              <PlusCircleOutlined /> {"Ajouter une réponse"}
            </Button>
          )}
        {question.type === SurveyWorkflowType.freetext && (
          <Checkbox
            onChange={handleUpdateIsQuestionOptional}
            disabled={!editMode}
            checked={question?.optional}
          >
            {t("survey.add_question.optional")}
          </Checkbox>
        )}
      </>
    );
  };

  return (
    <>
      <div
        id={`nbo-question${indexQuestion}`}
        className="d-flex flex-row justify-content-end align-items-stretch flex-wrap nbo-block"
      >
        <div className="flex-grow-1 d-flex align-items-center">
          <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
            <h4 className="text-primary mb-0">
              {t("survey.add_question.title", {
                index: indexQuestion + 1
              })}{" "}
              :{" "}
              {t(
                "survey.add_question.type." +
                  QuestionTypes.filter(quest => quest.name === question.type)[0]
                    .name
              )}
            </h4>
          </Divider>
        </div>
        {editMode && (
          <div className="d-flex align-items-center divider-sm-transparent">
            <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
              {handleAscendQuestion && (
                <Button
                  htmlType="reset"
                  className="btn-secondary m-2"
                  onClick={() => handleAscendQuestion(indexQuestion)}
                >
                  <UpOutlined /> {t("survey.global_buttons.question.up")}
                </Button>
              )}
              {handleDescendQuestion && (
                <Button
                  htmlType="reset"
                  className="btn-secondary m-2"
                  onClick={() => handleDescendQuestion(indexQuestion)}
                >
                  <DownOutlined /> {t("survey.global_buttons.question.down")}
                </Button>
              )}
              <SurveyAddQuestionButton
                buttonLabel={t("survey.global_buttons.question.insert")}
                handleAddQuestion={handleAddQuestion}
                index={indexQuestion}
              />
              <Popconfirm
                title={t("survey.add_question.cancel_confirmation")}
                okText={t("global.yes")}
                cancelText={t("global.no")}
                onConfirm={() => handleDeleteQuestion(indexQuestion)}
                placement="top"
                icon={<QuestionCircleOutlined />}
              >
                <Button htmlType="reset" className="btn-danger m-2">
                  <DeleteOutlined /> {t("survey.add_question.delete")}
                </Button>
              </Popconfirm>
            </Divider>
          </div>
        )}
      </div>
      {renderQuestionForm(indexQuestion)}
    </>
  );
};

QuestionBlock.defaultProps = {
  editMode: true
};
